import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import { ExceptionManager } from 'Core/logManager';

const SLICE_NAME = 'vidfleet/views/whiteListedDevices/redux';

const INIT_STATE = {
    Items: [],
    loading: false,
    total: 0,
    cameras: null, // cache del whitelist completo de ESNs (no paginable server-side)
    error: null,
    errorCode: null,
};

export const whiteListedDevicesRequest = createAsyncThunk(
    `${SLICE_NAME}/whiteListedDevicesRequest`,
    async (payload, { getState, rejectWithValue }) => {
        const { page = 0, rowsPerPage = 50, refresh = false } = payload || {};
        try {
            // 1) reusa el whitelist cacheado en el store; solo lo vuelve a pedir si no existe
            //    o si se pide refresh explícito (botón de refresh) — getCamerasOnWhiteList
            //    siempre trae TODOS los ESNs de una vez, no tiene paginación server-side.
            let cameras = getState().whiteListedDevicesRedux.cameras;
            if (!cameras || refresh) {
                let dataWhiteList = await clientQuery(
                    `query getCamerasOnWhiteList{ result: getCamerasOnWhiteList }`,
                    {},
                    endpoints.GRAPHQL_GENERAL
                );
                cameras = dataWhiteList.result ? (JSON.parse(dataWhiteList.result)?.cameras || []) : [];
            }
            const total = cameras.length;

            // 2) slice de la página pedida
            const offset = page * rowsPerPage;
            const pageCameras = cameras.slice(offset, offset + rowsPerPage);
            if (pageCameras.length === 0) {
                return { result: [], total, cameras };
            }

            // 3) searchUnits acotado solo a los ESNs de esta página
            let data = await clientQuery(
                `query searchUnits ($conditions: [UnitSearchInput], $sort: [UnitSortInput], $limit: Int, $offset: Int) {
                    result: searchUnits (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                        items { id companyId company { id name } label esn imei createdOn deviceModel { id name } timezone }
                        total
                    }
                }`,
                {
                    conditions: { fields: ["esn"], terms: pageCameras, exact: true },
                    limit: pageCameras.length,
                    offset: 0
                },
                endpoints.GRAPHQL_GENERAL
            );

            let devices = data.result?.items || [];
            let cams = pageCameras.map(x => ({ esn: x, device: devices.find(d => d.esn === x) }));
            return { result: cams, total, cameras };
        } catch (exc) {
            ExceptionManager(exc, `${SLICE_NAME}/whiteListedDevices`, 'whiteListedDevicesRequest');
            return rejectWithValue({ errorCode: exc.request?.status });
        }
    }
);

const whiteListedDevicesSlice = createSlice({
    name: SLICE_NAME,
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(whiteListedDevicesRequest.pending, (state) => {
                state.loading = true;
            })
            .addCase(whiteListedDevicesRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.Items = action.payload.result;
                state.total = action.payload.total;
                state.cameras = action.payload.cameras;
                state.error = null;
                state.errorCode = null;
            })
            .addCase(whiteListedDevicesRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = true;
                state.errorCode = action.payload?.errorCode;
            });
    }
});

export const { reset: whiteListedDevicesReset } = whiteListedDevicesSlice.actions;
export default whiteListedDevicesSlice.reducer;
